/**
 * MarketSearch Component - Suchkomponente für die Sidebar im Market-Bereich
 * 
 * Erlaubt Benutzern, Karten nach Namen zu durchsuchen und nach Elementen zu filtern.
 * Die Suchergebnisse werden an die Market-Komponente über den SearchService übermittelt.
 */

import { useState, useEffect, useCallback } from "react";
import { Box, TextField, IconButton, InputAdornment, Typography, useTheme, Tooltip, Divider } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { tokens } from "../../theme";
import searchService from "../../services/SearchService";
import { CARD_ELEMENTS, CARD_RARITIES, CARD_FOILS, CARD_TYPES } from "../../constants/filterConstants";
import SetFilter from "../../components/filters/SetFilter";
import BloodlineFilter from "../../components/filters/BloodlineFilter";

// Debounce-Funktion zur Reduzierung der Anfragen
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Constants moved to /src/constants/filterConstants.js

// Element-Filter-Komponente
const ElementFilter = ({ elements, selectedElements, onElementToggle, isMobile = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const iconSize = isMobile ? 18 : 24;
  const buttonPadding = isMobile ? '2px' : '4px';
  const containerPadding = isMobile ? 0.5 : 1;
  
  return (
    <Box mb={isMobile ? 1 : 2}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        sx={{ 
          backgroundColor: colors.primary[400],
          borderRadius: 1,
          p: containerPadding
        }}
      >
        {elements.map(element => (
          <Tooltip key={element.id} title={element.name} arrow placement="top">
            <IconButton
              size="small"
              onClick={() => onElementToggle(element.id)}
              sx={{ 
                padding: buttonPadding,
                opacity: selectedElements.includes(element.id) ? 1 : 0.6,
                '&:hover': {
                  opacity: 0.9
                },
                minWidth: isMobile ? '32px' : 'auto',
                minHeight: isMobile ? '32px' : 'auto'
              }}
            >
              <img 
                src={selectedElements.includes(element.id) ? element.activeIcon : element.inactiveIcon} 
                alt={element.name} 
                width={iconSize} 
                height={iconSize}
              />
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

// Type-Filter-Komponente (Units vs Archons) - Separate Buttons
const TypeFilter = ({ types, selectedTypes, onTypeToggle, isMobile = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const buttonHeight = isMobile ? 32 : 40;
  const iconSize = isMobile ? 18 : 24;
  const containerPadding = isMobile ? 0.5 : 1;
  const gap = isMobile ? 0.5 : 1;
  
  return (
    <Box mb={isMobile ? 1 : 2}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        sx={{ 
          backgroundColor: colors.primary[400],
          borderRadius: 1,
          p: containerPadding,
          gap: gap
        }}
      >
        {types.map(type => {
          const isSelected = selectedTypes.includes(type.id);
          
          return (
            <Tooltip key={type.id} title={type.name} arrow placement="top">
              <Box
                onClick={() => onTypeToggle(type.id)}
                sx={{
                  flex: 1,
                  height: buttonHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isSelected ? colors.blueAccent[600] : colors.primary[600],
                  borderRadius: '4px',
                  border: `2px solid ${isSelected ? colors.blueAccent[400] : colors.grey[600]}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  opacity: isSelected ? 1 : 0.8,
                  '&:hover': {
                    opacity: 1,
                    borderColor: isSelected ? colors.blueAccent[300] : colors.grey[400],
                    backgroundColor: isSelected ? colors.blueAccent[500] : colors.primary[500]
                  }
                }}
              >
                <img 
                  src={type.icon} 
                  alt={type.name} 
                  width={iconSize} 
                  height={iconSize}
                  style={{
                    filter: isSelected ? 'brightness(1.1)' : 'brightness(0.8)'
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

// Rarity-Filter-Komponente
const RarityFilter = ({ rarities, selectedRarities, onRarityToggle, isMobile = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const iconSize = isMobile ? 18 : 24;
  const buttonPadding = isMobile ? '2px' : '4px';
  const containerPadding = isMobile ? 0.5 : 1;
  
  return (
    <Box mb={isMobile ? 1 : 2}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        sx={{ 
          backgroundColor: colors.primary[400],
          borderRadius: 1,
          p: containerPadding
        }}
      >
        {rarities.map(rarity => {
          const isSelected = selectedRarities.includes(rarity.id);
          
          return (
            <Tooltip key={rarity.id} title={rarity.name} arrow placement="top">
              <IconButton
                size="small"
                onClick={() => onRarityToggle(rarity.id)}
                sx={{
                  padding: buttonPadding,
                  opacity: isSelected ? 1 : 0.6,
                  '&:hover': { opacity: 0.9 },
                  border: isSelected ? `2px solid ${colors.blueAccent[400]}` : 'none',
                  borderRadius: '4px',
                  minWidth: isMobile ? '32px' : 'auto',
                  minHeight: isMobile ? '32px' : 'auto'
                }}
              >
                <img src={rarity.icon} alt={rarity.name} width={iconSize} height={iconSize} />
              </IconButton>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

// Foil-Filter-Komponente
const FoilFilter = ({ foilTypes, selectedFoilTypes, onFoilTypeToggle, isMobile = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const buttonSize = isMobile ? 28 : 32;
  const fontSize = isMobile ? '10px' : '12px';
  const containerPadding = isMobile ? 0.5 : 1;
  
  // Mapping für Foil-Type-Farben und Labels
  const foilTypeStyles = {
    0: { // Regular
      bgColor: '#316099', 
      label: 'R',
      activeColor: '#4285f4' // Blau
    },
    1: { // Gold Foil
      bgColor: '#CA9C00',
      label: 'G',
      activeColor: '#ffc107' // Gold
    },
    2: { // Gold Arcana
      bgColor: '#CA9C00', 
      label: 'GV',
      activeColor: '#ff9800' // Gold/Orange
    },
    3: { // Black Foil
      bgColor: '#333333',
      label: 'B',
      activeColor: '#212121' // Schwarz
    },
    4: { // Black Arcana
      bgColor: '#4B0082',
      label: 'BV',
      activeColor: '#4B0082' // Dunkellila/Schwarz
    }
  };
  
  return (
    <Box mb={isMobile ? 1 : 2}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        sx={{ 
          backgroundColor: colors.primary[400],
          borderRadius: 1,
          p: containerPadding
        }}
      >
        {foilTypes.map(foilType => {
          const style = foilTypeStyles[foilType.id] || {};
          const isSelected = selectedFoilTypes.includes(foilType.id);
          
          return (
            <Tooltip key={foilType.id} title={foilType.name} arrow placement="top">
              <Box
                onClick={() => onFoilTypeToggle(foilType.id)}
                sx={{
                  width: buttonSize,
                  height: buttonSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isSelected ? style.activeColor : colors.primary[600],
                  borderRadius: '4px',
                  border: `2px solid ${isSelected ? style.activeColor : colors.grey[600]}`,
                  color: isSelected ? '#ffffff' : colors.grey[300],
                  fontWeight: 'bold',
                  fontSize: fontSize,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  opacity: isSelected ? 1 : 0.8,
                  '&:hover': {
                    opacity: 1,
                    borderColor: isSelected ? style.activeColor : colors.grey[400]
                  }
                }}
              >
                {style.label || foilType.name[0]}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

const MarketSearch = ({ isMobile = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [searchTerm, setSearchTerm] = useState("");
  const [resultsCount, setResultsCount] = useState(null);
  const [selectedElements, setSelectedElements] = useState([]);
  const [selectedRarities, setSelectedRarities] = useState([]);
  const [selectedFoilTypes, setSelectedFoilTypes] = useState([]);
  const [selectedSetCategories, setSelectedSetCategories] = useState(['conclave_arcana_core', 'conclave_arcana_escalation']); // Standard: Conclave Arcana Core + Escalation
  const [selectedBloodlines, setSelectedBloodlines] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]); // Neu: Unit vs Archon Filter

  // Setze den initialen Suchbegriff und Filter aus dem Service
  useEffect(() => {
    // Get initial search term
    const initialTerm = searchService.getSearchTerm();
    if (initialTerm) {
      setSearchTerm(initialTerm);
    }
    
    // Get initial filters
    const initialFilters = searchService.getFilters();
    if (initialFilters.elements) {
      setSelectedElements(initialFilters.elements);
    }
    if (initialFilters.rarities) {
      setSelectedRarities(initialFilters.rarities);
    }
    if (initialFilters.foilTypes) {
      setSelectedFoilTypes(initialFilters.foilTypes);
    }
    if (initialFilters.setCategories) {
      setSelectedSetCategories(initialFilters.setCategories);
    } else {
      // Set default Conclave Arcana Core + Escalation filter if no set categories are set
      const defaultCategories = ['conclave_arcana_core', 'conclave_arcana_escalation'];
      setSelectedSetCategories(defaultCategories);
      searchService.setFilters({
        ...initialFilters,
        setCategories: defaultCategories
      });
    }
    if (initialFilters.bloodlines) {
      setSelectedBloodlines(initialFilters.bloodlines);
    }
    if (initialFilters.types) {
      setSelectedTypes(initialFilters.types);
    }
    
    // Abonniere Suchergebniszahl-Updates vom Market
    const unsubscribe = searchService.subscribeToFilters((filters) => {
      if (filters.resultsCount !== undefined) {
        setResultsCount(filters.resultsCount);
      }
      if (filters.rarities) {
        setSelectedRarities(filters.rarities);
      }
      if (filters.foilTypes) {
        setSelectedFoilTypes(filters.foilTypes);
      }
      if (filters.setCategories) {
        setSelectedSetCategories(filters.setCategories);
      }
      if (filters.bloodlines) {
        setSelectedBloodlines(filters.bloodlines);
      }
      if (filters.types) {
        setSelectedTypes(filters.types);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Debounce für die Suche - wir wollen nicht bei jedem Tastenanschlag suchen
  const debouncedSearch = useCallback(
    debounce(term => {
      searchService.setSearchTerm(term);
    }, 300), // 300ms Verzögerung
    []
  );
  
  // Behandelt Änderungen im Suchfeld
  const handleSearchChange = (event) => {
    const newTerm = event.target.value;
    setSearchTerm(newTerm);
    // Suchbegriff verzögert an den Service senden
    debouncedSearch(newTerm);
  };

  // Suchfeld zurücksetzen
  const handleClearSearch = () => {
    setSearchTerm("");
    searchService.setSearchTerm("");
  };
  
  // Element-Filter umschalten
  const handleElementToggle = (elementId) => {
    setSelectedElements(prev => {
      let newElements;
      if (prev.includes(elementId)) {
        newElements = prev.filter(id => id !== elementId);
      } else {
        newElements = [...prev, elementId];
      }
      
      // Aktualisiere die Filter im SearchService
      const currentFilters = searchService.getFilters();
      searchService.setFilters({
        ...currentFilters,
        elements: newElements
      });
      
      return newElements;
    });
  };

  // Rarity-Filter umschalten
  const handleRarityToggle = (rarityId) => {
    setSelectedRarities(prev => {
      let newRarities;
      if (prev.includes(rarityId)) {
        newRarities = prev.filter(id => id !== rarityId);
      } else {
        newRarities = [...prev, rarityId];
      }
      // Filter im SearchService aktualisieren
      const currentFilters = searchService.getFilters();
      searchService.setFilters({
        ...currentFilters,
        rarities: newRarities
      });
      return newRarities;
    });
  };
  
  // Foil-Type-Filter umschalten
  const handleFoilTypeToggle = (foilTypeId) => {
    setSelectedFoilTypes(prev => {
      let newFoilTypes;
      if (prev.includes(foilTypeId)) {
        newFoilTypes = prev.filter(id => id !== foilTypeId);
      } else {
        newFoilTypes = [...prev, foilTypeId];
      }
      // Filter im SearchService aktualisieren
      const currentFilters = searchService.getFilters();
      searchService.setFilters({
        ...currentFilters,
        foilTypes: newFoilTypes
      });
      return newFoilTypes;
    });
  };
  
  // Set-Category-Filter umschalten
  const handleSetCategoryToggle = (categoryKey) => {
    setSelectedSetCategories(prev => {
      let newCategories;
      if (prev.includes(categoryKey)) {
        newCategories = prev.filter(key => key !== categoryKey);
      } else {
        newCategories = [...prev, categoryKey];
      }
      // Filter im SearchService aktualisieren
      const currentFilters = searchService.getFilters();
      searchService.setFilters({
        ...currentFilters,
        setCategories: newCategories
      });
      return newCategories;
    });
  };

  // Bloodline-Filter umschalten
  const handleBloodlineToggle = (bloodlineId) => {
    setSelectedBloodlines(prev => {
      let newBloodlines;
      if (prev.includes(bloodlineId)) {
        newBloodlines = prev.filter(id => id !== bloodlineId);
      } else {
        newBloodlines = [...prev, bloodlineId];
      }
      // Filter im SearchService aktualisieren
      const currentFilters = searchService.getFilters();
      searchService.setFilters({
        ...currentFilters,
        bloodlines: newBloodlines
      });
      return newBloodlines;
    });
  };

  // Type-Filter umschalten (Units vs Archons)
  const handleTypeToggle = (typeId) => {
    setSelectedTypes(prev => {
      let newTypes;
      if (prev.includes(typeId)) {
        newTypes = prev.filter(id => id !== typeId);
      } else {
        newTypes = [...prev, typeId];
      }
      // Filter im SearchService aktualisieren
      const currentFilters = searchService.getFilters();
      searchService.setFilters({
        ...currentFilters,
        types: newTypes
      });
      return newTypes;
    });
  };

  return (
    <Box sx={{ 
      p: isMobile ? 1 : 2,
      maxHeight: isMobile ? 'calc(100vh - 120px)' : 'none',
      overflowY: isMobile ? 'auto' : 'visible'
    }}>
      <Typography
        variant={isMobile ? "subtitle1" : "h6"}
        color={colors.grey[300]}
        sx={{ mb: isMobile ? 0.5 : 1 }}
      >
        Card Search
      </Typography>
      
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search cards by name..."
        value={searchTerm}
        onChange={handleSearchChange}
        size="small"
        sx={{
          backgroundColor: colors.primary[400],
          borderRadius: 1,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: colors.grey[500],
            },
            '&:hover fieldset': {
              borderColor: colors.grey[400],
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.greenAccent[500],
            },
          },
          '& .MuiInputBase-input': {
            fontSize: isMobile ? '14px' : '16px',
            padding: isMobile ? '8px 14px' : '12px 14px'
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ 
                color: colors.grey[300],
                fontSize: isMobile ? '18px' : '20px'
              }} />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton 
                onClick={handleClearSearch}
                size="small"
                sx={{ color: colors.grey[300] }}
              >
                <ClearIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      <Divider sx={{ 
        my: isMobile ? 1 : 2, 
        backgroundColor: colors.grey[800] 
      }} />
      
      {/* Set-Filter (hierarchisch) */}
      <SetFilter
        selectedCategories={selectedSetCategories}
        onCategoryToggle={handleSetCategoryToggle}
        isMobile={isMobile}
      />
      
      {/* Element-Filter */}
      <ElementFilter 
        elements={CARD_ELEMENTS} 
        selectedElements={selectedElements}
        onElementToggle={handleElementToggle}
        isMobile={isMobile}
      />
      
      {/* Type-Filter (Units vs Archons) */}
      <TypeFilter
        types={CARD_TYPES}
        selectedTypes={selectedTypes}
        onTypeToggle={handleTypeToggle}
        isMobile={isMobile}
      />
      
      {/* Rarity-Filter */}
      <RarityFilter
        rarities={CARD_RARITIES}
        selectedRarities={selectedRarities}
        onRarityToggle={handleRarityToggle}
        isMobile={isMobile}
      />
      
      {/* Foil-Filter */}
      <FoilFilter
        foilTypes={CARD_FOILS}
        selectedFoilTypes={selectedFoilTypes}
        onFoilTypeToggle={handleFoilTypeToggle}
        isMobile={isMobile}
      />
      
      {/* Bloodline-Filter */}
      <BloodlineFilter
        selectedBloodlines={selectedBloodlines}
        onBloodlineToggle={handleBloodlineToggle}
        isMobile={isMobile}
      />
      
      {/* Zeige Anzahl der Ergebnisse, wenn eine Suche durchgeführt wurde */}
      {resultsCount !== null && (
        <Box mt={isMobile ? 0.5 : 1} display="flex" justifyContent="flex-end">
          <Typography 
            variant={isMobile ? "caption" : "body2"} 
            color={colors.grey[300]}
          >
            {resultsCount} {resultsCount === 1 ? 'card' : 'cards'} found
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MarketSearch;
